import { motion } from "framer-motion";
import { Quote } from "lucide-react";

const CASES = [
    {
        client: "Northbeam Dental",
        industry: "Healthcare · Local",
        stat: "+312%",
        statLabel: "qualified leads in 90 days",
        quote: "The queue genuinely never empties now. We hired two more hygienists.",
        person: "Dr. Maya R. — Owner",
        services: ["Lead Gen", "Meta Ads", "Automation"],
    },
    {
        client: "Atlas & Form",
        industry: "Fitness Studio",
        stat: "1,240",
        statLabel: "appointments booked in 6 months",
        quote: "Follow-ups go out in seconds, even at 2am. Our no-show rate collapsed.",
        person: "Daniel K. — Founder",
        services: ["Appointment Setting", "AI & Automation"],
    },
    {
        client: "Loop Supply Co.",
        industry: "E-commerce",
        stat: "4.8x",
        statLabel: "ROAS across Meta & Google",
        quote: "One team runs the ads, the content and the site. Everything finally connects.",
        person: "Priya S. — CMO",
        services: ["Digital Marketing", "UGC & Content", "Websites"],
    },
    {
        client: "Harbor Legal",
        industry: "Professional Services",
        stat: "63%",
        statLabel: "more retained clients, year over year",
        quote: "The new intake flow and lifecycle emails did what three agencies couldn't.",
        person: "Tom H. — Partner",
        services: ["Copywriting", "Automation", "SEO"],
    },
];

const Card = ({ c, i }) => (
    <motion.div
        data-testid={`case-card-${i + 1}`}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.65, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
        className={`group flex flex-col justify-between border-2 border-ink/15 bg-[#FFFDF6] p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-inkblue hover:shadow-[7px_7px_0_#14171C] sm:p-8 ${
            i % 2 === 0 ? "rotate-[-0.4deg]" : "rotate-[0.4deg]"
        }`}
    >
        <div>
            <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-ink/50">{c.industry}</span>
                <Quote size={18} className="text-ink/25 transition-colors duration-300 group-hover:text-inkblue" />
            </div>
            <div className="mt-5 flex items-end gap-3">
                <span className="font-mono text-5xl font-bold leading-none text-inkblue sm:text-6xl">{c.stat}</span>
                <span className="pb-1 font-mono text-[11px] uppercase leading-tight tracking-wider text-ink/55">{c.statLabel}</span>
            </div>
            <p className="mt-5 text-base leading-relaxed text-ink/80">"{c.quote}"</p>
        </div>
        <div className="mt-7 border-t-2 border-dashed border-ink/15 pt-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="font-display text-lg font-bold text-ink">{c.client}</div>
                    <div className="font-mono text-[11px] uppercase tracking-wider text-ink/50">{c.person}</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                    {c.services.map((s) => (
                        <span key={s} className="border border-ink/20 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-ink/60">
                            {s}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    </motion.div>
);

export const CaseStudies = () => (
    <section id="results" data-testid="case-studies-section" className="py-24 lg:py-32">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
            <motion.div
                className="flex flex-wrap items-end justify-between gap-6"
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
                <div>
                    <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-inkblue">Case Studies — The Receipts</p>
                    <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
                        Queues we've <span className="highlighter px-1">already built.</span>
                    </h2>
                    <p className="mt-3 rotate-[-1.5deg] font-hand text-2xl text-inkblue">results, straight from the notebook</p>
                </div>
                <p className="max-w-xs font-mono text-[11px] uppercase leading-relaxed tracking-[0.18em] text-ink/50">
                    Sample engagements shown — your industry is next in line
                </p>
            </motion.div>
            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
                {CASES.map((c, i) => (
                    <Card key={c.client} c={c} i={i} />
                ))}
            </div>
        </div>
    </section>
);
